import { Stack } from '@fluentui/react';
import React from 'react';
import {
  PurchaseOrderAvailableTags,
  PurchaseOrderAvailableTagsVariables,
} from 'common/graphql/__generated__/PurchaseOrderAvailableTags';
import {
  ListPurchaseOrderTags,
  ListPurchaseOrderTagsVariables,
} from 'common/graphql/__generated__/ListPurchaseOrderTags';
import {
  EntityTagPurchaseOrderCreate,
  EntityTagPurchaseOrderCreateVariables,
} from 'common/graphql/__generated__/EntityTagPurchaseOrderCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import { EntityTagInput } from 'common/types/globalTypes';
import { useStyles } from 'common/components/ActionMenu/Tags/index.styles';
import { PurchaseOrderValues } from 'purchaseOrder/view/types';
import { useFormContext } from 'react-hook-form';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';

import LIST_PURCHASE_ORDER_TAGS from 'common/graphql/ListPurchaseOrderTags.graphql';
import PURCHASE_ORDER_AVAILABLE_TAGS from 'common/graphql/PurchaseOrderAvailableTags.graphql';
import ENTITY_TAG_PURCHASE_ORDER_CREATE from 'common/graphql/EntityTagPurchaseOrderCreate.graphql';
import ENTITY_TAG_DELETE from 'common/graphql/EntityTagDelete.graphql';

interface TagsProps {
  purchaseOrderId: string;
}

export const Tags: React.FC<TagsProps> = ({ purchaseOrderId }) => {
  const styles = useStyles();
  const {
    formState: { isDirty },
  } = useFormContext<PurchaseOrderValues>();

  // Extract available tags from the query data
  const extractAvailableTags = (
    data: PurchaseOrderAvailableTags | undefined
  ) => {
    return (
      data?.purchaseOrderAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? []
    );
  };

  // Extract entity tags from the query data
  const extractEntityTags = (data: ListPurchaseOrderTags | undefined) => {
    return (
      data?.purchaseOrder?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown', // Handle null
      })) ?? []
    );
  };

  // Variables for fetching available tags
  const getAvailableTagsVars = (
    entityId: string
  ): PurchaseOrderAvailableTagsVariables => ({
    id: entityId,
  });

  // Variables for fetching entity tags
  const getEntityTagsVars = (
    entityId: string
  ): ListPurchaseOrderTagsVariables => ({
    id: entityId,
  });

  // Variables for creating new tags
  const createTagsVars = (
    tags: EntityTagInput[]
  ): EntityTagPurchaseOrderCreateVariables => ({
    input: {
      entityTag: tags,
    },
  });

  // Variables for deleting tags
  const deleteTagsVars = (
    id: string,
    rowTimestamp: string
  ): EntityTagDeleteVariables => ({
    entityTagDeleteInput: {
      entityDelete: [{ id, rowTimestamp }],
    },
  });

  return (
    <Stack
      tokens={{ childrenGap: 15, padding: '0px 25px' }}
      className={styles.tagsContainer}
    >
      <EntityTagsSection<
        PurchaseOrderAvailableTags,
        PurchaseOrderAvailableTagsVariables,
        ListPurchaseOrderTags,
        ListPurchaseOrderTagsVariables,
        EntityTagPurchaseOrderCreate,
        EntityTagPurchaseOrderCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={purchaseOrderId}
        getAvailableTagsGql={PURCHASE_ORDER_AVAILABLE_TAGS}
        getEntityTagsGql={LIST_PURCHASE_ORDER_TAGS}
        addTagGql={ENTITY_TAG_PURCHASE_ORDER_CREATE}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
        isDisabled={isDirty}
      />
    </Stack>
  );
};
